import { AlertTriangle, CalendarClock, Check, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { formatPlanned } from '@/lib/planningDates'
import {
  hasWorkToDo,
  reserveSummaryText,
  type ReserveOutcome,
} from '@/lib/reservePlanSummary'
import type { PlannedReservation, ReservationPlan } from '@/api/proceduresApi'

const BLOCKED_LABEL: Record<NonNullable<PlannedReservation['blockedReason']>, string> = {
  no_unit_free: 'None free',
  no_run_time: 'No run time',
  already_held: 'Already held',
}

/**
 * Offered when a procedure is scheduled: book the run's equipment now?
 *
 * The per-step dialog asks this for one process field. This asks once for the
 * whole run — every form, every process field, every requirement — because
 * setting a schedule is the moment the operator has decided when the work
 * happens, and making them open each form to repeat that is the reason nobody
 * reserves anything until the day.
 *
 * **Partial, and it says so.** A busy unit blocks its own row and nothing else.
 * That is the opposite of the single-step reserve and of re-timing, which are
 * all-or-nothing; refusing a whole run because one centrifuge is taken would
 * make this useless on exactly the busy days it is for. The footer states the
 * count so nobody has to infer it from the list.
 */
export function ScheduleReserveDialog({
  open,
  procedureName,
  plan,
  loading,
  busy,
  error,
  onConfirm,
  onSkip,
}: {
  open: boolean
  procedureName: string
  /** Null while it is still being fetched. */
  plan: ReservationPlan | null
  loading: boolean
  busy: boolean
  error: string | null
  onConfirm: () => void
  /** Closes without booking. The schedule itself is already saved. */
  onSkip: () => void
}) {
  const rows = plan?.rows ?? []
  const bookable = plan?.bookable ?? 0
  // `already_held` is NOT a problem and is counted apart from the ones that
  // are. It now means held for exactly this window, so the row needs nothing
  // done to it — folding it into "cannot be booked" told the operator work was
  // outstanding on the machines that were already correct.
  const blocked = rows.filter(r => r.blockedReason && r.blockedReason !== 'already_held').length
  const held = rows.filter(r => r.blockedReason === 'already_held').length
  const moving = rows.filter(r => !r.blockedReason && r.supersedesReservationId).length
  const consumption = plan?.consumption ?? []
  const shortLines = plan?.shortLines ?? 0
  const holdableLines = plan?.holdableLines ?? 0

  // Every consequence of confirming, in one place. The button gates on BOTH
  // halves: `bookable` alone left a run whose machines were all held but whose
  // material was not with a disabled button and no way to hold the material.
  const outcome: ReserveOutcome = {
    bookable,
    moving,
    holdableLines,
    shortLines,
    alreadyHeld: held,
    blocked,
  }
  const canReserve = hasWorkToDo(outcome)

  return (
    <Dialog open={open} onOpenChange={o => !o && !busy && onSkip()}>
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 text-base'>
            <CalendarClock className='h-4 w-4 shrink-0 text-blue-600' />
            Reserve for this run?
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <p className='flex items-center gap-2 py-6 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Working out what this run needs…
          </p>
        ) : (
          <div className='space-y-3'>
            <p className='text-sm text-gray-700'>
              <span className='font-medium'>{procedureName}</span> is scheduled for{' '}
              <span className='font-mono font-semibold tabular-nums'>
                {plan ? formatPlanned(plan.startsAt) : '—'}
              </span>
              . Each unit is held from that plus its own prep.
            </p>

            {rows.length === 0 ? (
              <p className='rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600'>
                No process step in this run configures equipment, so there is nothing to reserve.
                The schedule is saved either way.
              </p>
            ) : (
              <div
                className={cn(
                  'overflow-y-auto rounded-md border border-gray-200',
                  // The two lists share the viewport: at 45vh each, a run with
                  // both would push the footer off-screen.
                  consumption.length > 0 ? 'max-h-[30vh]' : 'max-h-[45vh]',
                )}
              >
                <div className='sticky top-0 grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-x-3 border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-gray-500'>
                  <span>Step</span>
                  <span>Unit</span>
                  <span className='text-right'>Held</span>
                </div>
                <ul className='divide-y divide-gray-100'>
                  {rows.map(row => {
                    const isHeld = row.blockedReason === 'already_held'
                    return (
                      <li
                        key={`${row.submissionId}:${row.fieldId}:${row.requirementId}`}
                        className={cn(
                          'grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-x-3 px-3 py-1.5 text-xs',
                          // Amber is for a row that needs attention. An
                          // already-held row is settled, so it stays plain.
                          row.blockedReason && !isHeld && 'bg-amber-50/60',
                        )}
                      >
                        <div className='min-w-0'>
                          <p className='truncate text-gray-800'>{row.equipmentTypeName}</p>
                          <p className='truncate text-[10px] text-gray-500'>
                            {row.formName}
                            {/* This row moves a hold rather than taking a new
                                one — worth saying, because the machine is
                                already booked, just not for this time. */}
                            {row.supersedesReservationId && !row.blockedReason && ' · re-timing'}
                          </p>
                        </div>
                        <span
                          className={cn(
                            'truncate font-mono',
                            row.equipmentLabel ? 'text-gray-800' : 'text-amber-700',
                          )}
                        >
                          {row.equipmentLabel ?? BLOCKED_LABEL[row.blockedReason ?? 'no_unit_free']}
                        </span>
                        <span className='whitespace-nowrap text-right font-mono tabular-nums text-gray-500'>
                          {isHeld ? (
                            // The window it is actually held for. This is the
                            // row that needs nothing done, so it says so
                            // rather than showing a bare dash.
                            <span className='text-green-700'>
                              held {formatPlanned(row.startsAt)}
                            </span>
                          ) : row.blockedReason ? (
                            // Why, not just that — an operator told "none free"
                            // can act; one told nothing cannot.
                            <span className='text-amber-700'>
                              {row.busyUntil ? `busy until ${formatPlanned(row.busyUntil)}` : '—'}
                            </span>
                          ) : (
                            formatPlanned(row.startsAt)
                          )}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </div>
            )}

            {/* WHAT THE RUN WILL CONSUME — shown, never taken.
                Stock is committed by the signed Start; there is no inventory
                hold, and taking it here would be a regulated write with nobody's
                name on it. The value is in seeing a shortfall now, while there
                is still time to order, rather than on the day at Start. */}
            {consumption.length > 0 && (
              <div className='space-y-1.5'>
                <p className='text-[10px] font-medium uppercase tracking-wide text-gray-500'>
                  Materials this run will use
                </p>
                <div className='max-h-[22vh] overflow-y-auto rounded-md border border-gray-200'>
                  <ul className='divide-y divide-gray-100'>
                    {consumption.map(c => (
                      <li
                        key={`${c.submissionId}:${c.fieldId}:${c.requirementId}`}
                        className={cn(
                          'grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-x-3 px-3 py-1.5 text-xs',
                          c.isShort && 'bg-amber-50/60',
                        )}
                      >
                        <div className='min-w-0'>
                          <p className='truncate text-gray-800'>{c.itemLabel}</p>
                          <p className='truncate text-[10px] text-gray-500'>
                            {c.formName}
                            {c.lots.length > 0 && (
                              <> · {c.lots.map(l => `${l.lot} ×${l.qty}`).join(', ')}</>
                            )}
                          </p>
                        </div>
                        <span className='whitespace-nowrap font-mono tabular-nums text-gray-600'>
                          {c.qtyRequired}
                          {c.uom ? ` ${c.uom}` : ''}
                        </span>
                        {/* Three states, mirroring the equipment list above:
                            short (needs action), already held (settled), or
                            available to take. "In stock" alone could not tell
                            the last two apart. */}
                        <span
                          className={cn(
                            'whitespace-nowrap text-right text-[11px]',
                            c.isShort
                              ? 'font-medium text-amber-700'
                              : c.alreadyHeld
                                ? 'text-gray-500'
                                : 'text-green-700',
                          )}
                        >
                          {c.isShort
                            ? `short ${c.shortfall}`
                            : c.alreadyHeld
                              ? 'already held'
                              : 'in stock'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
                {/* What confirming does to the shelf, stated plainly. "Held"
                    and "taken" are different things and an operator has to know
                    which one this is: the material stays physically in place
                    and still counts as on-hand — it just stops being offered to
                    anything else. */}
                <p className='text-[11px] text-muted-foreground'>
                  Confirming holds these lots. They stay on the shelf and are deducted when the
                  step is started and signed.
                  {shortLines > 0 && (
                    <span className='font-medium text-amber-700'>
                      {' '}
                      {shortLines} {shortLines === 1 ? 'line is' : 'lines are'} short and will not
                      be held.
                    </span>
                  )}
                </p>
              </div>
            )}

            {/* The re-timing and already-held cases are stated once, in the
                summary above the footer — repeating them as banners here made
                three paragraphs say the same thing. */}
            {blocked > 0 && (
              <p className='flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900'>
                <AlertTriangle className='mt-px h-3.5 w-3.5 shrink-0 text-amber-600' />
                <span>
                  {blocked} of {rows.length} cannot be booked and will be left unreserved. The rest
                  are held — a busy unit does not stop the others.
                </span>
              </p>
            )}

            {error && (
              <p
                role='alert'
                className='rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800'
              >
                {error}
              </p>
            )}
          </div>
        )}

        {/* WHAT CONFIRMING DOES, spelled out. This replaced a bare `Reserve 5`
            on the button, which counted only machines — so the material half of
            the very same action went unmentioned. */}
        {!loading && plan && (
          <p className='rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700'>
            {reserveSummaryText(outcome)}
          </p>
        )}

        <DialogFooter className='gap-2 sm:gap-2'>
          {/* "Not now", not "Cancel": the schedule is already saved, and
              Cancel would imply this undoes it. */}
          <Button type='button' variant='ghost' onClick={onSkip} disabled={busy}>
            Not now
          </Button>
          <Button type='button' onClick={onConfirm} disabled={busy || loading || !canReserve}>
            {busy ? (
              <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />
            ) : (
              <Check className='mr-1.5 h-3.5 w-3.5' />
            )}
            {/* No count: the summary above says what happens, and a number here
                could only ever describe one half of it. */}
            {busy ? 'Reserving…' : canReserve ? 'Reserve' : 'Nothing to reserve'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
